import { AbstractControl } from '@angular/forms';
import { EMPTY, merge, Observable } from 'rxjs';
import { skip, tap } from 'rxjs/operators';
import { defaultRound, isAnyNan, isMinGreater, RoundFn } from '@marketiq/calc-core';
import { MessagePort } from '../ports/auto-calc-ports';

/**
 * Min > Max rate validation, moved out of the host `addListenersToRates` (common-listener-helper.ts).
 *
 * Subscribes the min + max controls; on each change, sets a named control error on the MIN control
 * when min > max (after rounding to the field precision), else clears it — preserving any other
 * errors via the {@link MessagePort}. Mirrors the host:
 *   isValid = !isAnyNan(min, max) && isMinGreater(min, max, column)
 *   isValid ? addControlError(min, code) : removeControlError(min, code)
 *
 * `isAnyNan` + `isMinGreater` are the byte-equivalent calc-core kernels (ported from `common-rate`).
 * Rounding is delegated to the injected `round` (host's locale pipeline) so the min>max comparison
 * matches the host for the wired rate columns; for Poland the injected round is the corrected
 * (Poland-aware) one, consistent with the shipped `autoCalcRound` policy.
 */
export function bindMinMaxValidation(
  min: AbstractControl | null | undefined,
  max: AbstractControl | null | undefined,
  errorCode: string,
  messages: MessagePort,
  round: RoundFn = defaultRound,
  shouldSkip = false
): Observable<unknown> {
  if (min == null || max == null) {
    return EMPTY;
  }
  let stream: Observable<unknown> = merge(min.valueChanges, max.valueChanges);
  if (shouldSkip) {
    stream = stream.pipe(skip(1));
  }
  return stream.pipe(
    tap(() => {
      const minGreater = !isAnyNan(min.value, max.value) && isMinGreater(min.value, max.value, round);
      if (minGreater) {
        messages.setControlError(min, errorCode);
      } else {
        messages.clearControlError(min, errorCode);
      }
    })
  );
}
